<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import AnalysisGroupEntityList from "./AnalysisGroupEntityList.vue";
import AnalysisKindSelector from "./AnalysisKindSelector.vue";
import CardField from "../cards/CardField.vue";
import EntityAddRow from "../cards/EntityAddRow.vue";
import DevInfoPopover from "@/components/canvas/analysis/DevInfoPopover.vue";
import { getObservationKindIcon } from "@/components/field-observations/observation-type";
import type {
  AnalysisEntity,
  AnalysisGroup,
  AnalysisGroupKind,
  AnalysisGroupStatus,
  PhotoAttachment,
} from "@/utils/plan-analysis/types";
import type { PlanAnalysisAiSuggestion, PlanAnalysisGroupEdit } from "@/types";

/**
 * Carte d'un groupe de détection dans l'onglet Analyse. En-tête épuré (n° +
 * titre proposé par l'IA) ; les métriques internes vivent dans l'infobulle
 * « Mode développeur », affichée sur la carte courante seulement. La carte
 * courante se déplie : type d'observation (désordre / vue d'ensemble /
 * commentaire), nom et commentaire éditables (badge « IA · Proposée » tant
 * que la proposition est intacte, popover pour la revoir/réappliquer), liste
 * des entités, ajout d'éléments. Un groupe ignoré reste listé, grisé — un
 * tap propose de le réafficher.
 */

const props = defineProps<{
  group: AnalysisGroup;
  number: number;
  status: AnalysisGroupStatus;
  isCurrent: boolean;
  suggestion?: PlanAnalysisAiSuggestion;
  /** Retouches utilisateur persistées (nom / commentaire). */
  edit?: PlanAnalysisGroupEdit;
  isValidating: boolean;
  /** Entités du groupe, fournies pour la carte courante (liste dépliable). */
  entities?: AnalysisEntity[];
  /** Nb d'entités sélectionnées sur le plan HORS du groupe (carte courante). */
  extraSelectedCount?: number;
  /** Mode « cliquez pour ajouter » actif sur cette carte. */
  addMode?: boolean;
}>();

const emit = defineEmits<{
  (e: "select"): void;
  (
    e: "validate",
    payload: {
      name?: string;
      description?: string;
      kind: AnalysisGroupKind;
    },
  ): void;
  (e: "ignore"): void;
  (e: "restore"): void;
  (e: "entity-select", handle: string): void;
  (e: "entity-remove", handle: string): void;
  (e: "entity-move", payload: { handle: string; targetGroupId: string }): void;
  (e: "add-selected"): void;
  (e: "toggle-add-mode"): void;
  (e: "edit-change", patch: PlanAnalysisGroupEdit): void;
}>();

const { t } = useTranslation();

/** La carte courante en attente est en mode édition légère. */
const isEditing = computed(
  () => props.isCurrent && props.status === "pending",
);

// ── Nom et commentaire : proposition IA retouchable ─────────────────────────

const localTitle = ref("");
const localFinding = ref("");

watch(
  isEditing,
  (editing) => {
    if (!editing) return;
    localTitle.value =
      props.edit?.title ?? props.suggestion?.title ?? "";
    localFinding.value =
      props.edit?.finding ?? props.suggestion?.finding ?? "";
  },
  { immediate: true },
);

/** Badge « IA · Proposée » : visible tant que la proposition est intacte. */
const titleIsProposal = computed(
  () =>
    !!props.suggestion?.title && localTitle.value === props.suggestion.title,
);
const findingIsProposal = computed(
  () =>
    !!props.suggestion?.finding &&
    localFinding.value === props.suggestion.finding,
);

/** Persiste la retouche ; revenir à l'exact texte proposé l'efface. */
const commitTitle = (): void => {
  emit("edit-change", {
    title:
      localTitle.value === (props.suggestion?.title ?? "")
        ? undefined
        : localTitle.value,
  });
};
const commitFinding = (): void => {
  emit("edit-change", {
    finding:
      localFinding.value === (props.suggestion?.finding ?? "")
        ? undefined
        : localFinding.value,
  });
};

const reapplyTitle = (): void => {
  localTitle.value = props.suggestion?.title ?? "";
  emit("edit-change", { title: undefined });
};
const reapplyFinding = (): void => {
  localFinding.value = props.suggestion?.finding ?? "";
  emit("edit-change", { finding: undefined });
};

const handleValidate = (): void => {
  const name = localTitle.value.trim();
  const description = localFinding.value.trim();
  emit("validate", {
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    kind: displayKind.value,
  });
};

// ── Type d'observation : nature dérivée, corrigeable ───────────────────────

const displayKind = computed<AnalysisGroupKind>(
  () => props.edit?.kind ?? props.group.kind,
);

/** Revenir à la nature dérivée efface la retouche. */
const handleKindChange = (kind: AnalysisGroupKind): void => {
  emit("edit-change", { kind: kind === props.group.kind ? undefined : kind });
};

// ── Affichage ───────────────────────────────────────────────────────────────

const defectDisplay = computed(() => {
  if (displayKind.value === "overview") {
    return t("canvas:analysis.card.overview");
  }
  if (displayKind.value === "comment" && !props.group.defectName) {
    return t("canvas:analysis.kinds.comment");
  }
  return (
    props.group.defectLabel ??
    props.group.defectName ??
    t("canvas:analysis.card.noDefect")
  );
});

/** Libellé d'un rattachement photo pour l'infobulle développeur. */
const photoLine = (photo: PhotoAttachment): string => {
  const rule = t(`canvas:analysis.photo.rule.${photo.rule}`);
  const distance =
    photo.distance === null ? "" : ` · ${(photo.distance * 100).toFixed(1)} %`;
  const alternatives = photo.alternatives.length
    ? ` · ${t("canvas:analysis.photo.alternativesCount", { count: photo.alternatives.length })}`
    : "";
  return `${photo.markerNumber ?? "?"} — ${rule}${distance}${alternatives}`;
};

/** Titre de la carte : retouche utilisateur, sinon IA, sinon le désordre. */
const displayTitle = computed(
  () => props.edit?.title ?? props.suggestion?.title ?? defectDisplay.value,
);

/** Commentaire affiché sous le titre sur les cartes repliées. */
const displayFinding = computed(
  () => props.edit?.finding ?? props.suggestion?.finding ?? null,
);

/** Mesures agrégées sur tout le groupe (longueur linéaire, surface). */
const totals = computed(() => {
  let length = 0;
  let area = 0;
  for (const entry of props.group.context.entities) {
    length += entry.totalLength ?? 0;
    area += entry.totalArea ?? 0;
  }
  const round = (v: number): number => Math.round(v * 100) / 100;
  return { length: round(length), area: round(area) };
});

const formatTime = (epochMs: number): string =>
  new Date(epochMs).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const timeLabel = computed(() => {
  const range = props.group.timeRange;
  if (!range) return null;
  const start = formatTime(range.start);
  const end = formatTime(range.end);
  return start === end
    ? t("canvas:analysis.card.drawnAt", { time: start })
    : t("canvas:analysis.card.drawnBetween", { start, end });
});

const confidencePercent = computed(() =>
  Math.round(props.group.confidence * 100),
);
</script>

<template>
  <div
    class="analysis-card"
    :class="[`status-${status}`, { current: isCurrent, manual: group.manual }]"
    @click="$emit('select')"
  >
    <!-- En-tête : n°, titre IA, infobulle développeur -->
    <div class="d-flex align-items-center gap-2">
      <span class="card-number">{{ number }}</span>
      <PhosphorIcon
        :name="getObservationKindIcon(displayKind)"
        :size="14"
        weight="bold"
        class="type-icon"
      />
      <span class="fw-medium text-truncate flex-grow-1">
        {{ displayTitle }}
      </span>
      <DevInfoPopover v-if="isCurrent">
        <div class="dev-row">
          <span class="dev-label">{{ t("canvas:analysis.devInfo.note") }}</span>
          {{ defectDisplay }}
        </div>
        <div class="dev-row">
          {{ t("canvas:analysis.card.entities", { count: group.tracedCount }) }}
        </div>
        <div v-if="totals.length > 0" class="dev-row">
          {{ t("canvas:analysis.card.totalLength", { value: totals.length }) }}
        </div>
        <div v-if="totals.area > 0" class="dev-row">
          {{ t("canvas:analysis.card.totalArea", { value: totals.area }) }}
        </div>
        <div v-if="timeLabel" class="dev-row">{{ timeLabel }}</div>
        <div v-if="group.texts.length" class="dev-row">
          {{ t("canvas:analysis.card.texts") }} {{ group.texts.join(" · ") }}
        </div>
        <div v-if="group.markerNumbers.length" class="dev-row">
          {{ t("canvas:analysis.card.photos") }}
          {{ group.markerNumbers.join(", ") }}
        </div>
        <div
          v-for="photo in group.photoAttachments ?? []"
          :key="photo.handle"
          class="dev-row dev-photo"
        >
          <PhosphorIcon name="camera" :size="10" />
          {{ photoLine(photo) }}
        </div>
        <div v-if="group.vignetteName" class="dev-row">
          <span class="dev-label">
            {{ t("canvas:analysis.devInfo.vignette") }}
          </span>
          {{ group.vignetteName }}
        </div>
        <div class="dev-row">
          <span class="dev-label">
            {{ t("canvas:analysis.card.confidence") }}
          </span>
          {{ confidencePercent }}%
        </div>
        <div v-if="suggestion?.splitSuggested" class="dev-row">
          <PhosphorIcon name="scissors" :size="10" />
          {{ t("canvas:analysis.card.splitSuggested") }}
        </div>
        <div v-if="group.aiReasoning" class="dev-row dev-reasoning">
          {{ group.aiReasoning }}
        </div>
      </DevInfoPopover>
    </div>

    <!-- Carte repliée : le commentaire sous le titre, tronqué -->
    <div v-if="!isEditing && displayFinding" class="card-finding mt-1">
      {{ displayFinding }}
    </div>

    <!-- Carte courante à valider : type, nom + commentaire éditables -->
    <template v-if="isEditing">
      <CardField class="mt-3" :label="t('canvas:analysis.card.kindLabel')">
        <AnalysisKindSelector
          :model-value="displayKind"
          @update:model-value="handleKindChange"
        />
      </CardField>

      <CardField class="mt-3" :label="t('canvas:analysis.card.nameLabel')">
        <template #aside>
          <span v-if="titleIsProposal" class="proposal-badge">
            <PhosphorIcon name="sparkle" :size="10" weight="fill" />
            {{ t("canvas:analysis.card.proposed") }}
          </span>
          <el-popover
            v-else-if="suggestion?.title"
            trigger="click"
            :width="240"
            placement="top-end"
          >
            <template #reference>
              <button type="button" class="proposal-link">
                <PhosphorIcon name="sparkle" :size="11" />
                {{ t("canvas:analysis.card.viewProposal") }}
              </button>
            </template>
            <div class="proposal-popup" @click.stop>
              <div class="proposal-popup-title">
                {{ t("canvas:analysis.card.viewProposal") }}
              </div>
              <div class="proposal-popup-text">{{ suggestion.title }}</div>
              <DRBtn
                :label="t('canvas:analysis.card.reapply')"
                variant="outline"
                size="xs"
                @click="reapplyTitle"
              />
            </div>
          </el-popover>
        </template>
        <el-input
          v-model="localTitle"
          :placeholder="t('canvas:analysis.card.namePlaceholder')"
          @change="commitTitle"
        />
      </CardField>

      <CardField class="mt-3" :label="t('canvas:analysis.card.findingLabel')">
        <template #aside>
          <span v-if="findingIsProposal" class="proposal-badge">
            <PhosphorIcon name="sparkle" :size="10" weight="fill" />
            {{ t("canvas:analysis.card.proposed") }}
          </span>
          <el-popover
            v-else-if="suggestion?.finding"
            trigger="click"
            :width="260"
            placement="top-end"
          >
            <template #reference>
              <button type="button" class="proposal-link">
                <PhosphorIcon name="sparkle" :size="11" />
                {{ t("canvas:analysis.card.viewProposal") }}
              </button>
            </template>
            <div class="proposal-popup" @click.stop>
              <div class="proposal-popup-title">
                {{ t("canvas:analysis.card.viewProposal") }}
              </div>
              <div class="proposal-popup-text">{{ suggestion.finding }}</div>
              <DRBtn
                :label="t('canvas:analysis.card.reapply')"
                variant="outline"
                size="xs"
                @click="reapplyFinding"
              />
            </div>
          </el-popover>
        </template>
        <el-input
          v-model="localFinding"
          type="textarea"
          :autosize="{ minRows: 2, maxRows: 5 }"
          :placeholder="t('canvas:analysis.card.findingPlaceholder')"
          @change="commitFinding"
        />
      </CardField>

      <!-- Entités du groupe + ajout d'éléments (ligne dans la liste) -->
      <AnalysisGroupEntityList
        v-if="entities?.length"
        class="mt-3"
        :entities="entities"
        :photo-attachments="group.photoAttachments"
        @select="emit('entity-select', $event)"
        @remove="emit('entity-remove', $event)"
        @move="emit('entity-move', $event)"
      />
      <EntityAddRow
        :add-mode="!!addMode"
        :extra-selected-count="extraSelectedCount ?? 0"
        @add-selected="emit('add-selected')"
        @toggle-add-mode="emit('toggle-add-mode')"
      />

      <!-- Footer : Ignorer / Valider, centrés -->
      <div
        class="actions-row d-flex justify-content-center gap-3 mt-3"
        @click.stop
      >
        <DRBtn
          :label="t('canvas:analysis.actions.ignore')"
          variant="outline-white"
          size="sm"
          @click="$emit('ignore')"
        />
        <DRBtn
          icon="bi-check-lg"
          :label="t('canvas:analysis.actions.validate')"
          variant="success"
          size="sm"
          :loading="isValidating"
          @click="handleValidate"
        />
      </div>
    </template>

    <!-- Ignorée : grisée ; un tap (courante) propose de la réafficher -->
    <div
      v-else-if="status === 'ignored' && isCurrent"
      class="d-flex justify-content-center mt-2"
      @click.stop
    >
      <DRBtn
        icon="bi-arrow-counterclockwise"
        :label="t('canvas:analysis.actions.restore')"
        variant="outline-white"
        size="xs"
        @click="$emit('restore')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.analysis-card {
  border: 1px solid var(--canvas-border);
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  background-color: var(--canvas-bg);
  transition:
    border-color 0.15s ease,
    opacity 0.15s ease;

  // Carte courante : fond légèrement teinté de la couleur de l'onglet.
  &.current {
    border-color: var(--dr-report);
    box-shadow: 0 0 0 1px var(--dr-report);
    background-color: color-mix(
      in srgb,
      var(--dr-report) 14%,
      var(--canvas-bg)
    );
  }
  &.status-validated {
    border-color: var(--bs-success);
    opacity: 0.85;
  }
  // Créé à la main : sarcelle au lieu du violet des détections.
  &.manual.current {
    border-color: var(--bs-teal);
    box-shadow: 0 0 0 1px var(--bs-teal);
    background-color: color-mix(in srgb, var(--bs-teal) 12%, var(--canvas-bg));
  }
  &.manual .card-number {
    background-color: var(--bs-teal);
  }
  &.status-ignored {
    opacity: 0.45;
    filter: grayscale(0.6);
  }
}

.card-number {
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  background-color: var(--dr-report);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 20px;
  text-align: center;

  .status-validated & {
    background-color: var(--bs-success);
  }
}

// Icône du type, toujours dans la couleur de l'onglet (violet = IA).
.type-icon {
  color: var(--dr-report-light);
  flex-shrink: 0;
}

.dev-row {
  padding: 2px 0;
  opacity: 0.9;
}

.dev-label {
  opacity: 0.65;
  margin-right: 4px;
}

.dev-reasoning {
  font-style: italic;
  border-top: 1px solid var(--canvas-border);
  margin-top: 4px;
  padding-top: 4px;
}

.dev-photo {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 6px;
  opacity: 0.8;
}

.proposal-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: transparent;
  color: var(--dr-report-light);
  font-size: 0.68rem;
  font-weight: 600;
}

.proposal-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  background: transparent;
  color: var(--dr-report-light);
  font-size: 0.68rem;
  padding: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.proposal-popup-title {
  font-weight: 600;
  font-size: 0.75rem;
  color: var(--dr-report);
  margin-bottom: 4px;
}

.proposal-popup-text {
  font-size: 0.78rem;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.actions-row {
  border-top: 1px solid var(--canvas-border);
  padding-top: 8px;
  cursor: default;
}

.card-finding {
  font-size: 0.78rem;
  opacity: 0.7;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
